import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Sidebar from './Sidebar';
import TopNavbar from './TopNavbar';

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Check if current page is public (no sidebar needed)
  const isPublicPage = location.pathname === '/login' || location.pathname === '/register';

  // Handle auth redirects in useEffect
  useEffect(() => {
    if (!isAuthenticated && !isPublicPage && !loading) {
      navigate('/login');
    }
  }, [isAuthenticated, isPublicPage, navigate, loading]);

  // Always render public pages without app shell
  if (isPublicPage) {
    return <>{children}</>;
  }

  // Show nothing while checking auth state
  if (loading) {
    return null;
  }

  // Return null while redirecting (not authenticated, not a public page)
  if (!isAuthenticated) {
    return null;
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f8fafc' }}>
      {/* Desktop Sidebar */}
      {isDesktop && (
        <div style={{ flexShrink: 0 }}>
          <Sidebar />
        </div>
      )}

      {/* Mobile Header - Simplified for mobile */}
      {!isDesktop && (
        <header
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            height: '56px',
            background: '#ffffff',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            alignItems: 'center',
            padding: '0 16px',
            zIndex: 1000,
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button
              onClick={() => setMobileSidebarOpen(!mobileSidebarOpen)}
              style={{
                width: '36px',
                height: '36px',
                background: 'transparent',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#64748b',
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            </button>
            <div
              style={{
                width: '32px',
                height: '32px',
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                <polyline points="22,6 12,13 2,6" />
              </svg>
            </div>
            <span style={{ fontSize: '1rem', fontWeight: 700, color: '#0f172a' }}>
              MailFlow
            </span>
          </div>
        </header>
      )}

      {/* Mobile Sidebar Overlay */}
      {!isDesktop && mobileSidebarOpen && (
        <div
          onClick={() => setMobileSidebarOpen(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 999,
          }}
        />
      )}

      {/* Mobile Sidebar */}
      {!isDesktop && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: mobileSidebarOpen ? 0 : '-280px',
            width: '280px',
            height: '100%',
            background: 'white',
            zIndex: 1000,
            transition: 'left 250ms ease-out',
            boxShadow: '2px 0 8px rgba(0,0,0,0.1)',
          }}
        >
          <div style={{ paddingTop: '56px', height: '100%', overflowY: 'auto' }}>
            <Sidebar />
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <main
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          overflowX: 'hidden',
        }}
      >
        {/* Top Navbar - Only on desktop */}
        {isDesktop && <TopNavbar />}

        {/* Page Content */}
        <div
          style={{
            flex: 1,
            padding: isDesktop ? '0' : '56px 0 0 0',
            background: '#f8fafc',
          }}
        >
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;
